import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../../api/client';
import AdminModal from '../../../components/admin/AdminModal';

const emptyBrief = {
  name: '',
  summary: '',
  features: '',
  setup: '',
  commands: '',
  links: '',
  importString: '',
  youtubeId: '',
  extra: '',
  exampleSlug: '',
};

export default function AiToolPageModal({ open, onClose, onDraft, pages }) {
  const [brief, setBrief] = useState(emptyBrief);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    apiGet('/admin/ai/status').then(setStatus).catch(() => setStatus({ configured: false }));
  }, [open]);

  const set = (key) => (event) => setBrief({ ...brief, [key]: event.target.value });

  const generate = async () => {
    setError('');
    setBusy(true);
    try {
      const draft = await apiPost('/admin/ai/tool-page', { ...brief, exampleSlug: brief.exampleSlug || null });
      onDraft(draft);
      setBrief(emptyBrief);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const publishedPages = (pages || []).filter((p) => p.published && (p.content?.sections?.length || 0) > 0);

  return (
    <AdminModal
      open={open}
      as="form"
      onSubmit={generate}
      onClose={onClose}
      size="lg"
      title="Generate a tool page with AI"
      subtitle="Describe the tool in your own words. AI drafts the whole page, then you review it in the editor before saving."
      footer={(
        <>
          {error && <span className="admin-modal__error">{error}</span>}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !status?.configured || !brief.name.trim()}>
            {busy ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Writing page…</> : <><i className="fas fa-magic" aria-hidden="true" /> Generate draft</>}
          </button>
        </>
      )}
    >
      {status && !status.configured && (
        <p className="admin-ai-panel__setup">
          AI is not set up yet. Add <code>OPENAI_API_KEY</code>, <code>ANTHROPIC_API_KEY</code> or <code>GEMINI_API_KEY</code> to <code>api/.env</code>.
        </p>
      )}
      <div className="admin-modal-fields">
        <div className="admin-field-row">
          <label className="admin-field">
            <span>Tool name</span>
            <input value={brief.name} onChange={set('name')} required autoFocus placeholder="The Ocarina" />
          </label>
          <label className="admin-field">
            <span>Copy the tone of</span>
            <select value={brief.exampleSlug} onChange={set('exampleSlug')}>
              <option value="">Longest published page (default)</option>
              {publishedPages.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
            </select>
          </label>
        </div>
        <label className="admin-field">
          <span>What it does</span>
          <textarea rows={3} value={brief.summary} onChange={set('summary')} placeholder="An OBS overlay that lets chat play Zelda songs on a staff…" />
        </label>
        <label className="admin-field">
          <span>Key features</span>
          <textarea rows={3} value={brief.features} onChange={set('features')} placeholder="One per line" />
        </label>
        <label className="admin-field">
          <span>Setup steps</span>
          <textarea rows={4} value={brief.setup} onChange={set('setup')} placeholder="Open the customizer, enable Streamer.bot WebSocket, add Browser Source 1920x400…" />
        </label>
        <div className="admin-field-row">
          <label className="admin-field">
            <span>Chat commands (optional)</span>
            <textarea rows={3} value={brief.commands} onChange={set('commands')} placeholder="!lurk – everyone – starts lurk timer" />
          </label>
          <label className="admin-field">
            <span>Links</span>
            <textarea rows={3} value={brief.links} onChange={set('links')} placeholder="Customizer: https://tools.zemaxi.com/…&#10;Download: https://…" />
          </label>
        </div>
        <div className="admin-field-row">
          <label className="admin-field">
            <span>Tutorial YouTube ID (optional)</span>
            <input value={brief.youtubeId} onChange={set('youtubeId')} placeholder="dQw4w9WgXcQ" />
          </label>
          <label className="admin-field">
            <span>Streamer.bot import string (optional)</span>
            <input value={brief.importString} onChange={set('importString')} placeholder="U0JBRR+LCAAAAAAABA…" />
          </label>
        </div>
        <label className="admin-field">
          <span>Anything else (FAQ answers, requirements, known issues)</span>
          <textarea rows={3} value={brief.extra} onChange={set('extra')} />
        </label>
      </div>
    </AdminModal>
  );
}
